import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, Send, SquarePen, Trash2 } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PartnerEmail } from '@/types/partner';
import { hasPermission } from '@/utils/permissions';
import { useAuth } from '@/contexts/auth';
import {
  formatClientDateTime,
  getClientDisplayName,
  getClientStatus,
} from '../utils/clientDisplay';

interface ClientTableProps {
  clients: PartnerEmail[];
  isLoading: boolean;
  error: string | null;
  onResend: (client: PartnerEmail) => void;
  onEdit: (client: PartnerEmail) => void;
  onDelete: (client: PartnerEmail) => void;
}

export function ClientTable({
  clients,
  isLoading,
  error,
  onResend,
  onEdit,
  onDelete,
}: ClientTableProps) {
  const { user } = useAuth();
  const canEdit = hasPermission(user, 'edit_partners');

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Nome</TableHead>
          <TableHead>Whatsapp</TableHead>
          <TableHead>Convite enviado</TableHead>
          <TableHead className="text-center">Status</TableHead>
          {canEdit && <TableHead className="text-center">Ações</TableHead>}
        </TableRow>
      </TableHeader>
      <TableBody>
        {isLoading ? (
          <TableRow>
            <TableCell colSpan={canEdit ? 5 : 4} className="p-8 text-center">
              <div className="flex items-center justify-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Carregando clientes...</span>
              </div>
            </TableCell>
          </TableRow>
        ) : error ? (
          <TableRow>
            <TableCell colSpan={canEdit ? 5 : 4} className="p-8 text-center text-red-600">
              {error}
            </TableCell>
          </TableRow>
        ) : clients.length === 0 ? (
          <TableRow>
            <TableCell colSpan={canEdit ? 5 : 4} className="p-8 text-center text-gray-500">
              Nenhum cliente cadastrado
            </TableCell>
          </TableRow>
        ) : (
          clients.map((client) => {
            const status = getClientStatus(client);
            return (
              <TableRow key={client.id}>
                <TableCell className="p-4">
                  <div className="font-medium text-gray-900">{getClientDisplayName(client)}</div>
                  <div className="text-sm text-gray-500">{client.email}</div>
                </TableCell>
                <TableCell className="p-4 text-gray-600">{client.whatsapp || '—'}</TableCell>
                <TableCell className="p-4 text-gray-600">
                  {formatClientDateTime(client.created_at)}
                </TableCell>
                <TableCell className="p-4 text-center">
                  <Badge
                    className={
                      status === 'ACTIVE'
                        ? 'bg-green-100 text-green-800 hover:bg-green-100'
                        : 'bg-amber-100 text-amber-800 hover:bg-amber-100'
                    }
                  >
                    {status === 'ACTIVE' ? 'Ativo' : 'Pendente'}
                  </Badge>
                </TableCell>
                {canEdit && (
                  <TableCell className="p-4">
                    <div className="flex items-center justify-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        title={
                          status === 'ACTIVE'
                            ? 'Usuário já ativo — reenvio indisponível'
                            : 'Reenviar link de criação de senha'
                        }
                        aria-label={
                          status === 'ACTIVE'
                            ? 'Usuário já ativo — reenvio indisponível'
                            : 'Reenviar link de criação de senha'
                        }
                        disabled={status === 'ACTIVE'}
                        onClick={() => onResend(client)}
                      >
                        <Send className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        title="Editar cliente"
                        onClick={() => onEdit(client)}
                      >
                        <SquarePen className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                        title="Excluir cliente"
                        onClick={() => onDelete(client)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  );
}
